import React, { useEffect, useRef, useState } from 'react';
import useWindowWidth from 'hooks/use-window-width';
import { getRelativeTime } from 'utils/date-from-now';
import { buildClassName } from 'utils/build-class-name';
import { useThemeContext } from 'contexts';
import { ThemeNames } from 'types/themes';
import { TryScrambleText } from 'utils/text-scrambler';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import styles from './style.module.scss';
import { StandardStatus } from '@zoocasa/go-search';

import type Listing from 'data/listing';

export type ListingTimestampData = Pick<Listing, 'soldAt' | 'expiredAt' | 'updatedOn' | 'addedAt' | 'standardStatus'>;

/** i18next context suffixes on `listing_card.timestamp_label`. '' falls back to the bare `{{date}}`. */
type TimestampContext = '' | 'active' | 'expired' | 'rented' | 'sold';

/**
 * The en wording of each `listing_card.timestamp_label` context variant, used as
 * the i18next `defaultValue` so a not-yet-loaded namespace renders real copy
 * rather than the raw key.
 */
const CONTEXT_FALLBACKS: Record<TimestampContext, string> = {
  '': '{{date}}',
  active: 'Added {{date}}',
  expired: 'Removed {{date}}',
  rented: 'Leased {{date}}',
  sold: 'Sold {{date}}',
};

interface Props {
  listing: ListingTimestampData;
  isSmallCard?: boolean;
  className?: string;
  isMinimal?: boolean;
  testId?: string;
  isVisible?: boolean;
  shouldScramble: boolean;
  /** Rentals read "Leased" where sales read "Sold". */
  isRental?: boolean;
}

export default function ListingTimestamp({ listing, isSmallCard, isMinimal, className, testId, isVisible, shouldScramble, isRental = false }: Props) {
  const { soldAt, expiredAt, updatedOn, addedAt, standardStatus } = listing;
  const { t } = useTranslation(Namespaces.COMMON);
  const windowWidth = useWindowWidth();
  const timestampElement = useRef<HTMLDivElement>(null);
  const [isWrapping, setIsWrapping] = useState(false);
  // expiredAt is a historical field the backend does not clear when a listing is
  // relisted as Active, so it can't be trusted over a known standardStatus — only
  // fall back to it when standardStatus itself is unknown.
  const isExpired = standardStatus != null
    ? standardStatus === StandardStatus.STANDARD_STATUS_EXPIRED
      || standardStatus === StandardStatus.STANDARD_STATUS_CANCELED
      || standardStatus === StandardStatus.STANDARD_STATUS_WITHDRAWN
    : !!expiredAt;
  const timestamp = soldAt || isExpired && (updatedOn || addedAt) || addedAt;
  const isFutureDate = Boolean(timestamp) && new Date() < new Date(timestamp);
  const blurStyle = !isVisible ? 'blur' : '';
  const { themeName } = useThemeContext();
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;

  /**
   * "2 days ago" via Intl.RelativeTimeFormat, so the wording follows the active
   * locale instead of being assembled in English.
   *
   * `narrow` abbreviates the unit for small cards ("4d ago"). Note it keeps the
   * "ago" suffix, where the previous hand-built small-card label stripped it
   * ("4 days ") — narrow is shorter overall, and unlike that string surgery it
   * stays correct in every locale.
   */
  const relativeDate = (date: Date, isNarrow: boolean) => {
    const [value, unit] = getRelativeTime(new Date(date));
    return t(LOCALE_COMMON_PATHS.RELATIVE_TIME, {
      val: value,
      range: unit,
      numeric: 'auto',
      defaultValue: '{{val, relativetime}}',
      ...(isNarrow ? { style: 'narrow' } : {}),
    });
  };

  /** Wraps the relative date in its status prefix ("Added …"), or renders it bare when suppressed. */
  const labelFor = (date: Date, context: TimestampContext, suppressPrefix: boolean) => {
    const resolvedContext = suppressPrefix ? '' : context;
    return t(LOCALE_COMMON_PATHS.LISTING_CARD_TIMESTAMP_LABEL, {
      date: relativeDate(date, false),
      context: resolvedContext,
      defaultValue: CONTEXT_FALLBACKS[resolvedContext],
    });
  };

  const dateLabel = (() => {
    if (isFutureDate) {
      return undefined;
    }

    // NOTE: the sold/added branches suppress their prefix on `isMinimal` while the
    // expired branch suppresses on `isWrapping`. That asymmetry is carried over
    // verbatim from the pre-i18n version (where each branch ANDed in a
    // `styles['small-card']` class this stylesheet never defined, so the term was
    // always undefined); it is preserved here so this change stays copy-only.
    if (soldAt) {
      return isSmallCard ? relativeDate(soldAt, true) : labelFor(soldAt, isRental ? 'rented' : 'sold', Boolean(isMinimal));
    } else if (isExpired) {
      return isSmallCard ? relativeDate(updatedOn || addedAt, true) : labelFor(updatedOn || addedAt, 'expired', isWrapping);
    } else if (addedAt) {
      return isSmallCard ? relativeDate(addedAt, true) : labelFor(addedAt, 'active', Boolean(isMinimal));
    }
    return undefined;
  })();

  useEffect(() => {
    if (dateLabel && timestampElement.current) {
      setIsWrapping(timestampElement.current.offsetHeight > 15);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth]);

  return <>{dateLabel && <p className={buildClassName(styles.component, className, blurStyle, isExpTheme && styles.exp)} data-testid={testId} ref={timestampElement}>{TryScrambleText(dateLabel, shouldScramble)}</p>}</>;
}
